// ViewPurchaseReturn.js
import React, { useEffect, useState, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import UniversalInvoicePrint from "./UniversalInvoicePrint";
import api from "../Service/api";
import "./Purchases.css";

function ViewPurchaseReturn() {

  const getLocalDate = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updateInvoice, setUpdateInvoice] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [vendorList, setVendorList] = useState([]);
  const [selectedVendorBalance, setSelectedVendorBalance] = useState(null);

  const printRef = useRef();
  const [printInvoice, setPrintInvoice] = useState(null);
  const [loggedInUser, setLoggedInUser] = useState(null);

  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [fullScreenImage, setFullScreenImage] = useState(null);
  const [startDate, setStartDate] = useState(getLocalDate)
  const [endDate, setEndDate] = useState(getLocalDate)
  const [isUpdating, setIsUpdating] = useState(false);

  const [loadingProducts, setLoadingProducts] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingVendors, setLoadingVendors] = useState(true);

  const formatToDateOnly = (datetime) => {
    if (!datetime) return '-';
    if (typeof datetime === 'string' && datetime.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return datetime;
    }
    try {
      const date = new Date(datetime);
      if (isNaN(date.getTime())) return '-';
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (e) {
      return '-';
    }
  };

  // Helper to sort invoices descending by invoice number
const sortInvoicesDesc = (arr) => {
  return arr.sort((a, b) => (parseInt(b.invoiceNumber) || 0) - (parseInt(a.invoiceNumber) || 0));
};

  const getFilteredStock = (stockByWeight, category, sizeValue, lengthValue) => {
    if (!stockByWeight) return null;
    const filtered = {};
    const lowerCat = category?.toLowerCase();
    for (const [display, qty] of Object.entries(stockByWeight)) {
      if (lowerCat === 'roll') {
        if (sizeValue) {
          const displaySize = display.match(/^(\d+(?:\.\d+)?)/);
          if (displaySize && parseFloat(displaySize[1]) === parseFloat(sizeValue)) {
            filtered[display] = qty;
          }
        } else {
          filtered[display] = qty;
        }
      } else if (lowerCat === 'reel' || lowerCat === 'tota') {
        if (lengthValue) {
          const lengthMatch = display.match(/\|\s*(\d+(?:\.\d+)?)m/);
          if (lengthMatch && parseFloat(lengthMatch[1]) === parseFloat(lengthValue)) {
            filtered[display] = qty;
          }
        } else {
          filtered[display] = qty;
        }
      } else {
        filtered[display] = qty;
      }
    }
    return filtered;
  };

  useEffect(() => {
    const fetchVendors = async () => {
      setLoadingVendors(true);
      try {
        let allVendors = [];
        let currentPage = 0;
        const pageSize = 100;
        let hasMore = true;
        while (hasMore) {
          const res = await api.get("/product/getAll", {
            params: { page: currentPage, size: pageSize }
          });
          if (res.data.code === 1) {
            const vendors = res.data.additionalDetail;
            if (vendors && vendors.length > 0) {
              allVendors = [...allVendors, ...vendors];
              currentPage++;
              hasMore = vendors.length === pageSize;
            } else {
              hasMore = false;
            }
          } else {
            hasMore = false;
          }
        }
        setVendorList(allVendors);
      } catch (err) {
        console.error("Error fetching vendors:", err);
      } finally {
        setLoadingVendors(false);
      }
    };
    fetchVendors();
  }, []);

  const refreshProductList = async () => {
    setLoadingProducts(true);
    try {
      const res = await api.get("/product/names-prices");
      if (res.data.code === 1) {
        setAllProducts(res.data.additionalDetail);
      }
    } catch (err) {
      console.error("Error refreshing product list", err);
    } finally {
      setLoadingProducts(false);
    }
  };

  useEffect(() => {
    const phone = localStorage.getItem("userPhone");
    if (!phone) return;
    api.get(`/authentication/userData/${phone}`)
      .then(res => setLoggedInUser(res.data.additionalDetail))
      .catch(err => console.error("USER DATA API ERROR:", err));
  }, []);

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: printInvoice ? `Invoice-${printInvoice.invoiceNumber}` : "Invoice",
  });

  const handlePrintInvoice = (invoice) => {
    const copy = JSON.parse(JSON.stringify(invoice));
    copy.user = loggedInUser;
    copy.products = (copy.sales || copy.products || []).map((p) => ({
      ...p,
      calculatedTotal: computeProductTotal(p),
    }));
    copy.payBill = invoice.payBill ?? invoice.amountPaid ?? 0;
    copy.gstPercentage = invoice.gstPercentage || 0;
    copy.gstAmount = invoice.gstAmount || 0;
    copy.totalBeforeGst = invoice.totalBeforeGst || 0;
    copy.grandTotal = computeInvoiceGrandTotal(copy);
    setPrintInvoice(copy);
    setTimeout(() => handlePrint(), 300);
  };

  useEffect(() => {
    loadPurchases();
    loadProductList();
    loadCategoryList();
  }, []);

  useEffect(() => {
    if (loading === false) {
      loadPurchases();
    }
  }, [startDate, endDate]);

  const loadPurchases = async () => {
    try {
      const params = { status: "Return" };
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const res = await api.get("/product/products/by-category-and-name", { params });
      const data = res.data.code === 1 ? res.data.additionalDetail || [] : [];
const sortedData = sortInvoicesDesc(data);   // ← add this line
setInvoices(sortedData);
setFilteredInvoices(sortedData);
    } catch (err) {
      console.error("Error fetching purchases", err);
      setInvoices([]);
      setFilteredInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  const resetFilters = () => {
    setStartDate("");
    setEndDate("");
    setSearchText("");
    loadPurchases();
  };

  const loadProductList = async () => {
    setLoadingProducts(true);
    try {
      const res = await api.get("/product/names-prices");
      if (res.data.code === 1) {
        setAllProducts(res.data.additionalDetail);
      }
    } catch (err) {
      console.error("Error loading product list", err);
      setAllProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  const loadCategoryList = async () => {
    setLoadingCategories(true);
    try {
      const res = await api.get("/api/category/getCategories");
      setCategoryList(res.data.additionalDetail || []);
    } catch (err) {
      setCategoryList([
        { categoryName: "packet" }, { categoryName: "roll" }, { categoryName: "reel" },
        { categoryName: "box" }, { categoryName: "kg" }, { categoryName: "bottle" },
      ]);
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleSearch = (text) => {
    setSearchText(text);
    if (!text) {
      setFilteredInvoices(invoices);
      return;
    }
    const filtered = invoices.filter(inv =>
  inv.vendorName?.toLowerCase().includes(text.toLowerCase()) ||
  inv.products?.some(p => p.productName?.toLowerCase().includes(text.toLowerCase())) ||
  inv.invoiceNumber?.toString().includes(text) ||
  inv.invoiceNumber?.toString().toLowerCase().includes(text.toLowerCase())
);
setFilteredInvoices(sortInvoicesDesc(filtered));   // ← sort here
  };

  const computeProductTotal = (prod) => {
    const category = (prod.category || "").toLowerCase();
    const price = parseFloat(prod.price) || 0;
    let quantity = 0;
    if (prod.quantity !== undefined && prod.quantity !== null) {
      quantity = parseFloat(prod.quantity) || 0;
    } else if (prod.returnedQuantity !== undefined && prod.returnedQuantity !== null) {
      quantity = parseFloat(prod.returnedQuantity) || 0;
    } else {
      quantity = 0;
    }
    
    const size = parseFloat(prod.size) || 1;
    const ktae = parseFloat(prod.ktae) || 1;
    const gram = parseFloat(prod.gram) || 1;
    let total = 0;
    
    if (category === "packet") total = (quantity * size * ktae * gram * price) / 15500;
    else if (category === "roll") total = size * quantity * price;
    else if (category === "reel" || category === "tota") total = quantity * price;
    else if (category === "box") total = price * quantity;
    else total = price * quantity;
    
    if (prod.generalDiscount) total -= parseFloat(prod.generalDiscount) || 0;
    return Number(total.toFixed(2));
  };

  const openUpdateForm = (invoice) => {
    const copy = JSON.parse(JSON.stringify(invoice || {}));
    copy.products = (copy.products || []).map(p => ({
      ...p,
      quantity: p.returnedQuantity ?? p.quantity ?? 0,
      price: p.price ?? 0,
      size: p.size ?? "",
      length: p.length ?? "",
      ktae: p.ktae ?? "",
      gram: p.gram ?? "",
      boxSize: p.boxSize ?? "",
      particular: p.particular ?? "",
      calculatedTotal: computeProductTotal(p),
    }));
    copy.invoiceDiscount = invoice.invoiceDiscount ?? 0;
    copy.invoiceRent = invoice.invoiceRent ?? 0;
    copy.amountPaid = invoice.amountPaid ?? 0;
    copy.purchaseDate = invoice.purchaseDate || invoice.invoiceDate || getLocalDate();
    copy.grandTotal = computeInvoiceGrandTotal(copy);
    setSelectedVendorBalance(null);
    setUpdateInvoice(copy);
  };

  const handleUpdateVendorChange = (e) => {
    const name = e.target.value;
    setUpdateInvoice(prev => ({ ...prev, vendorName: name }));
    const vendor = vendorList.find(v => v.vendorName === name);
    if (vendor && vendor.currentBalance !== undefined) {
      setSelectedVendorBalance(vendor.currentBalance);
    } else {
      setSelectedVendorBalance(null);
    }
  };

  useEffect(() => {
    if (!updateInvoice) return;
    const recalculated = computeInvoiceGrandTotal(updateInvoice);
    if (recalculated !== updateInvoice.grandTotal) {
      setUpdateInvoice(prev => ({ ...prev, grandTotal: recalculated }));
    }
  }, [updateInvoice?.products, updateInvoice?.invoiceDiscount, updateInvoice?.invoiceRent, updateInvoice?.amountPaid, updateInvoice?.gstPercentage]);

  const computeInvoiceGrandTotal = (invoice) => {
    if (!invoice) return 0;
    const productsTotal = (invoice.products || []).reduce((total, p) => {
      const category = (p.category || "").toLowerCase();
      const price = parseFloat(p.price) || 0;
      let quantity = 0;
      if (p.quantity !== undefined && p.quantity !== null) {
        quantity = parseFloat(p.quantity) || 0;
      } else if (p.returnedQuantity !== undefined && p.returnedQuantity !== null) {
        quantity = parseFloat(p.returnedQuantity) || 0;
      } else {
        quantity = 0;
      }
      
      const size = parseFloat(p.size) || 1;
      const ktae = parseFloat(p.ktae) || 1;
      const gram = parseFloat(p.gram) || 1;
      let productTotal = 0;
      if (category === "packet") productTotal = (quantity * price * size * ktae * gram) / 15500;
      else if (category === "roll") productTotal = quantity * price * size;
      else if (category === "reel" || category === "tota") productTotal = quantity * price;
      else if (category === "box") productTotal = quantity * price;
      else productTotal = quantity * price;
      if (p.generalDiscount) productTotal -= parseFloat(p.generalDiscount) || 0;
      return total + productTotal;
    }, 0);
    const discount = parseFloat(invoice.invoiceDiscount) || 0;
    const rent = parseFloat(invoice.invoiceRent) || 0;
    const amountBeforeGst = productsTotal - discount + rent;
    const gstPercentage = parseFloat(invoice.gstPercentage) || 0;
    const gstAmount = (amountBeforeGst * gstPercentage) / 100;
    let grandTotal = amountBeforeGst + gstAmount;
    const amountPaid = parseFloat(invoice.amountPaid) || 0;
    grandTotal -= amountPaid;
    return Number(grandTotal.toFixed(2));
  };

  useEffect(() => {
  const handleWheel = (e) => {
    if (e.target.type === 'number') {
      e.preventDefault();
    }
  };
  document.addEventListener('wheel', handleWheel, { passive: false });
  return () => document.removeEventListener('wheel', handleWheel);
}, []);
  const handleUpdateProductChange = (index, field, value) => {
    setUpdateInvoice(prev => {
      if (!prev) return prev;
      
      const products = [...prev.products];
      const prod = { ...products[index], [field]: value };
      
      if (field === "productName") {
        const match = allProducts.find(ap => ap.productName?.toLowerCase() === value.toLowerCase());
        if (match) {
          prod.price = match.purchasePrice ?? prod.price;
          prod.category = match.category ?? prod.category;
          if (match.category && match.category.toLowerCase() === 'roll' && match.size) {
            prod.size = match.size;
          }
        }
      }
      
      const currentCategory = (prod.category || "").toLowerCase();
      if (field === "quantity" && (currentCategory === "reel" || currentCategory === "tota")) {
        const weightValue = parseFloat(value) || 0;
        prod.size = weightValue;
      }
      
      if (field === "category") {
        const cat = (value || "").toLowerCase();
        if (cat !== "packet") {
          prod.ktae = "";
          prod.gram = "";
        }
        if (cat !== "box") {
          prod.boxSize = "";
        }
        if (!["packet", "roll", "reel", "box", "tota"].includes(cat)) {
          prod.size = "";
        }
        if (cat !== 'reel' && cat !== 'tota') {
          prod.length = "";
        }
      }
      
      products[index] = prod;
      
      const productsTotal = products.reduce((sum, p) => sum + computeProductTotal(p), 0);
      const discount = parseFloat(prev.invoiceDiscount) || 0;
      const rent = parseFloat(prev.invoiceRent) || 0;
      const amountBeforeGst = productsTotal - discount + rent;
      const gstPercentage = parseFloat(prev.gstPercentage) || 0;
      const gstAmount = (amountBeforeGst * gstPercentage) / 100;
      const amountPaid = parseFloat(prev.amountPaid) || 0;
      const grandTotal = amountBeforeGst + gstAmount - amountPaid;
      
      return {
        ...prev,
        products,
        totalBeforeGst: Number(productsTotal.toFixed(2)),
        amountBeforeGst: Number(amountBeforeGst.toFixed(2)),
        gstAmount: Number(gstAmount.toFixed(2)),
        grandTotal: Number(grandTotal.toFixed(2))
      };
    });
  };

  const handleUpdateInvoiceField = (field, value) => {
    if (!updateInvoice) return;
    const updated = { ...updateInvoice, [field]: value };
    updated.grandTotal = computeInvoiceGrandTotal(updated);
    setUpdateInvoice(updated);
  };

  const saveUpdate = async () => {
    if (!updateInvoice) return alert("No invoice to update");
    setIsUpdating(true);
    try {
      const toSend = JSON.parse(JSON.stringify(updateInvoice));
      toSend.products = (toSend.products || []).map((p) => {
        const cat = (p.category || "").toLowerCase();
        const clean = { ...p };
        clean.returnedQuantity = parseFloat(clean.quantity) || 0;
        if ((cat === "reel" || cat === "tota") && (!clean.size || clean.size === 0)) {
          clean.size = parseFloat(clean.quantity) || 0;
        }
        delete clean.quantity;
        if (!["packet", "roll", "reel", "box", "tota"].includes(cat)) {
          delete clean.size;
        }
        if (cat !== "packet") {
          delete clean.ktae;
          delete clean.gram;
        }
        if (cat !== "box") {
          delete clean.boxSize;
        }
        if (cat !== 'reel' && cat !== 'tota') {
          delete clean.length;
        }
        clean.calculatedTotal = computeProductTotal(clean);
        return clean;
      });
      const invoiceNum = toSend.invoiceNumber;
      await api.put(`/product/productUpdated/${invoiceNum}`, toSend, { 
        params: { status: "Return", purchaseDate: toSend.purchaseDate } 
      });
      alert("Invoice updated!");
      setUpdateInvoice(null);
      setSelectedVendorBalance(null);
      await refreshProductList();
      loadPurchases();
    } catch (err) {
      console.error("Update failed", err);
      alert("Update failed! See console.");
    } finally {
      setIsUpdating(false);
    }
  };

  const deleteInvoice = async (invoiceNum) => {
    if (!window.confirm(`Delete invoice ${invoiceNum}?`)) return;
    try {
      await api.delete(`/product/deleteProduct/${invoiceNum}`, { params: { status: "Return" } });
      alert("Deleted!");
      loadPurchases();
    } catch (err) {
      console.error("Delete failed", err);
      alert("Delete failed");
    }
  };

  const formatQuantity = (quantity) => {
    if (quantity === null || quantity === undefined) return '0';
    const num = typeof quantity === 'number' ? quantity : parseFloat(quantity) || 0;
    if (Math.abs(num - Math.round(num)) < 0.000001) return Math.round(num).toString();
    return num.toFixed(2);
  };

  const showWeightLabel = updateInvoice?.products?.some(p => {
    const cat = (p.category || "").toLowerCase();
    return cat === "reel" || cat === "tota";
  });

  const isInitialLoading = loadingProducts || loadingCategories || loadingVendors;

  if (loading) return <div className="loading-text">Loading…</div>;

  const renderUpdateStockDisplay = (selectedProduct, productName, category, sizeValue, lengthValue) => {
    if (!selectedProduct || !productName) return null;
    const cat = (selectedProduct.category || "").toLowerCase();
    const isReelTota = cat === 'reel' || cat === 'tota';
    const isRollCat = cat === 'roll';
    const isPacketCat = cat === 'packet';
    let stockData = null;
    if ((isReelTota || isRollCat) && selectedProduct.stockByWeight) {
      stockData = getFilteredStock(selectedProduct.stockByWeight, cat, sizeValue, lengthValue);
    } else if (isPacketCat && selectedProduct.packetStockByVariant) {
      stockData = selectedProduct.packetStockByVariant;
    }
    if (!stockData || Object.keys(stockData).length === 0) return null;
    const stockEntries = Object.entries(stockData).filter(([_, qty]) => qty > 0);
    if (stockEntries.length === 0) {
      return <div style={{ color: '#ff4444', backgroundColor: '#ffebee', padding: '6px 8px', borderRadius: '4px', marginTop: '4px', border: '1px solid #ff4444', fontSize: '11px' }}>
        <strong>⚠️ Out of Stock</strong>
      </div>;
    }
    return <div style={{ color: '#0a8938', backgroundColor: '#f8f9fa', padding: '6px 8px', borderRadius: '4px', marginTop: '4px', border: '1px solid #0a8938', fontSize: '11px' }}>
      <strong style={{ fontSize: '10px', display: 'block', marginBottom: '4px' }}>📊 Available Stock:</strong>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
        {stockEntries.map(([display, qty]) => 
          <span key={display} style={{ backgroundColor: '#e8f5e9', padding: '2px 6px', borderRadius: '12px', fontSize: '10px', fontWeight: 'bold' }}>
            {display}: <span style={{ color: '#2e7d32' }}>{qty}</span>
          </span>
        )}
      </div>
    </div>;
  };

  return (
    <div className="purchase-container">
      <h2 className="page-title">📄 View Purchase Return</h2>

      {isInitialLoading && (
        <div style={{ textAlign: 'center', padding: '10px', backgroundColor: '#e3f2fd', marginBottom: '10px', borderRadius: '4px' }}>
          ⏳ Loading data... {loadingProducts && "Products "} {loadingCategories && "Categories "} {loadingVendors && "Vendors "}
        </div>
      )}

      <div style={{ marginBottom: 20 }}>
        <input
          type="text"
          placeholder="Search by product, vendor, or invoice number..."
          value={searchText}
          onChange={(e) => handleSearch(e.target.value)}
          className="read-only-input"
          style={{ width: 300, padding: 6 }}
          disabled={isInitialLoading}
        />
      </div>

      <div style={{ marginBottom: 20, display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label style={{ fontWeight: '500', color: '#000', fontSize: '14px' }}>From:</label>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="read-only-input" style={{ padding: '6px', borderRadius: '4px' }} disabled={isInitialLoading} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label style={{ fontWeight: '500', color: '#000', fontSize: '14px' }}>To:</label>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="read-only-input" style={{ padding: '6px', borderRadius: '4px' }} min={startDate} disabled={isInitialLoading} />
        </div>
        <button onClick={resetFilters} className="remove-btn" style={{ padding: '6px 12px', fontSize: '14px', marginLeft: '10px' }} disabled={isInitialLoading}>Clear Filters</button>
        {(startDate || endDate || searchText) && (
          <span style={{ fontSize: '14px', color: '#555', marginLeft: '10px' }}>
            Showing {filteredInvoices.length} of {invoices.length} purchases
            {searchText && ` for "${searchText}"`}
            {startDate && ` from ${startDate}`}
            {endDate && ` to ${endDate}`}
          </span>
        )}
      </div>

      {filteredInvoices.length === 0 && !loading && (
        <p style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
          {searchText || startDate || endDate ? "No purchases found matching your search/filters." : "No purchases found."}
        </p>
      )}

      {filteredInvoices.map((invoice) => {
        const products = invoice.products || [];
        const categories = products.map(p => (p.category || "").toLowerCase());
        const hasPacket = categories.includes("packet");
        const hasRoll = categories.some(c => ["roll"].includes(c));
        const hasBox = categories.includes("box");
        const hasParticularColumn = categories.some(c => !["roll", "packet", "box"].includes(c));
        const hasLengthColumn = categories.some(c => c === "reel" || c === "tota");
        // Show Size column if ANY product needs it (packet, roll, or reel/tota)
        const showSizeColumn = hasPacket || hasRoll || hasLengthColumn;

        return (
          <div key={invoice.invoiceNumber} className="invoice-box">
            <h3 className="invoice-title" style={{ color: "#000" }}>Invoice #{invoice.invoiceNumber} – {invoice.vendorName}</h3>

            {invoice.invoiceImagePath && (
              <div style={{ marginBottom: "10px", cursor: "pointer" }}>
                <img
                  src={invoice.invoiceImagePath}
                  alt={`Invoice ${invoice.invoiceNumber}`}
                  style={{ maxWidth: "200px", maxHeight: "200px", border: "1px solid #ccc", padding: "5px", objectFit: "contain" }}
                  onClick={() => setFullScreenImage(invoice.invoiceImagePath)}
                />
              </div>
            )}

            <div style={{ marginBottom: "10px", display: "flex", gap: "10px" }}>
              <button className="save-btn" onClick={() => openUpdateForm(invoice)} disabled={isInitialLoading}>Update</button>
              <button className="remove-btn" onClick={() => deleteInvoice(invoice.invoiceNumber)} disabled={isInitialLoading}>Delete</button>
              <button className="print-btn" onClick={() => handlePrintInvoice(invoice)} disabled={!invoice || isInitialLoading}>Print</button>
            </div>

            <div className="purchase-table-container">
              <div className="table-scroll-wrapper">
                <table className="purchase-table">
                  <thead>
                    <tr>
                      <th>Product</th><th>Category</th><th>Return Qty</th><th>Rate</th>
                      <th>Return Date</th><th>Updated Date</th>
                      {/* SINGLE Size column - shown if packet, roll, or reel/tota products exist */}
                      {showSizeColumn && <th>Size</th>}
                      {hasPacket && (<><th>Ktae</th><th>Gram</th></>)}
                      {hasBox && (<><th>Box Size</th><th>Particular</th></>)}
                      {hasParticularColumn && <th>Particular</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((p, idx) => {
                      const category = (p.category || "").toLowerCase();
                      const isReelOrTota = category === 'reel' || category === 'tota';
                      const isRoll = category === 'roll';
                      const isPacket = category === 'packet';
                      
                      // Determine what to show in the Size column
                      let sizeDisplay = '-';
                      if (isPacket || isRoll) {
                        sizeDisplay = p.size || '-';
                      } else if (isReelOrTota) {
                        sizeDisplay = p.length || '-';
                      }
                      
                      return (
                        <tr key={idx}>
                          <td>{p.productName}</td>
                          <td>{p.category}</td>
                          <td>{formatQuantity(p.returnedQuantity || p.quantity)}</td>
                          <td>{p.price}</td>
                          <td>{formatToDateOnly(p.returnTime || p.productEntryTime)}</td>
                          <td>{formatToDateOnly(p.recordUpdatedTime)}</td>
                          {/* SINGLE Size column */}
                          {showSizeColumn && <td>{sizeDisplay}</td>}
                          {hasPacket && (
                            <>
                              <td>{isPacket ? (p.ktae || '-') : '-'}</td>
                              <td>{isPacket ? (p.gram || '-') : '-'}</td>
                            </>
                          )}
                          {hasBox && (
                            <>
                              <td>{category === 'box' ? (p.boxSize || '-') : '-'}</td>
                              <td>{category === 'box' ? (p.particular || '-') : '-'}</td>
                            </>
                          )}
                          {hasParticularColumn && (
                            <td>{!["roll", "packet", "box"].includes(category) ? (p.particular || '-') : '-'}</td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* UPDATE FORM */}
            {updateInvoice && updateInvoice.invoiceNumber === invoice.invoiceNumber && (
              <div className="invoice-box" style={{ background: "#fff7e6", marginTop: 12 }}>
                <h3 className="invoice-title">Update Invoice #{updateInvoice.invoiceNumber}</h3>

                <div style={{ marginBottom: 15, padding: "0 10px" }}>
                  <label style={{ fontWeight: "bold", marginRight: 10 }}>Return Date:</label>
                  <input
                    type="date"
                    value={updateInvoice.purchaseDate || getLocalDate()}
                    onChange={(e) => handleUpdateInvoiceField("purchaseDate", e.target.value)}
                    className="read-only-input"
                    style={{ width: "200px" }}
                    max={new Date().toISOString().split('T')[0]}
                    disabled={isUpdating}
                  />
                </div>

                <div className="purchase-table-container">
                  <div className="table-scroll-wrapper">
                    <table className="purchase-table">
                      <thead>
                        <tr>
                          <th>Product Name</th><th>Category</th>
                          <th>{showWeightLabel ? "Weight (kg)" : "Return Qty"}</th>
                          <th>Rate</th>
                          {(() => {
                            const updateCategories = updateInvoice.products.map(p => (p.category || "").toLowerCase());
                            const showPacketCols = updateCategories.includes("packet");
                            const showRollOnly = updateCategories.some(c => ["roll"].includes(c));
                            const showBoxCols = updateCategories.includes("box");
                            const showParticularCol = updateCategories.some(c => !["roll", "packet", "box"].includes(c));
                            const showLengthCol = updateCategories.some(c => c === "reel" || c === "tota");
                            const showSize = showPacketCols || showRollOnly || showLengthCol;
                            return (
                              <>
                                {showSize && <th>Size</th>}
                                {showPacketCols && (<><th>Ktae</th><th>Gram</th></>)}
                                {showBoxCols && (<><th>Box Size</th><th>Particular</th></>)}
                                {showParticularCol && !showBoxCols && <th>Particular</th>}
                              </>
                            );
                          })()}
                        </tr>
                      </thead>
                      <tbody>
                        {updateInvoice.products.map((p, idx) => {
                          const cat = (p.category || "").toLowerCase();
                          const updateCategories = updateInvoice.products.map(p => (p.category || "").toLowerCase());
                          const showPacketColumns = updateCategories.includes("packet");
                          const showRollOnly = updateCategories.some(c => ["roll"].includes(c));
                          const showBoxColumns = updateCategories.includes("box");
                          const showParticularColumn = updateCategories.some(c => !["roll", "packet", "box"].includes(c));
                          const showLengthColumn = updateCategories.some(c => c === "reel" || c === "tota");
                          const showSize = showPacketColumns || showRollOnly || showLengthColumn;
                          const selectedProduct = allProducts.find(ap => ap.productName === p.productName);
                          const stockQuantity = selectedProduct?.stockQuantity;
                          const stockByWeight = selectedProduct?.stockByWeight;
                          const packetStockByVariant = selectedProduct?.packetStockByVariant;
                          const isReelOrTota = cat === 'reel' || cat === 'tota';
                          const isPacket = cat === 'packet';
                          const isRoll = cat === 'roll';

                          return (
                            <tr key={idx}>
                              <td style={{ position: 'relative' }}>
                                <input
                                  list={`products-${idx}`}
                                  value={p.productName || ""}
                                  onChange={(e) => handleUpdateProductChange(idx, "productName", e.target.value)}
                                  className="read-only-input"
                                  disabled={isUpdating}
                                />
                                <datalist id={`products-${idx}`}>
                                  {allProducts.map((ap, i) => {
                                    const apCategoryLower = (ap.category || "").toLowerCase();
                                    const isApReelOrTota = apCategoryLower === 'reel' || apCategoryLower === 'tota';
                                    const isApPacket = apCategoryLower === 'packet';
                                    if (isApReelOrTota && ap.stockByWeight && Object.keys(ap.stockByWeight).length > 0) {
                                      const nonZeroStocks = Object.entries(ap.stockByWeight).filter(([_, qty]) => qty > 0);
                                      if (nonZeroStocks.length > 0) {
                                        return <option key={i} value={ap.productName}>{ap.productName} [{nonZeroStocks.map(([weight, qty]) => `${weight}: ${qty}`).join(', ')}]</option>;
                                      } else {
                                        return <option key={i} value={ap.productName}>{ap.productName} (Out of Stock)</option>;
                                      }
                                    } else if (isApPacket && ap.packetStockByVariant && Object.keys(ap.packetStockByVariant).length > 0) {
                                      const nonZeroStocks = Object.entries(ap.packetStockByVariant).filter(([_, qty]) => qty > 0);
                                      if (nonZeroStocks.length > 0) {
                                        return <option key={i} value={ap.productName}>{ap.productName} [{nonZeroStocks.map(([variant, qty]) => `${variant}: ${qty}`).join(', ')}]</option>;
                                      } else {
                                        return <option key={i} value={ap.productName}>{ap.productName} (Out of Stock)</option>;
                                      }
                                    } else {
                                      return <option key={i} value={ap.productName}>{ap.productName} {ap.stockQuantity !== undefined ? `(Stock: ${ap.stockQuantity})` : ''}{ap.category === 'roll' && ap.size ? ` [Size: ${ap.size}]` : ''}</option>;
                                    }
                                  })}
                                </datalist>

                                {renderUpdateStockDisplay(selectedProduct, p.productName, p.category, p.size, p.length)}

                                {isPacket && selectedProduct && !isUpdating && (() => {
                                  const totalStock = selectedProduct.stockQuantity || 0;
                                  const variantEntries = packetStockByVariant ? Object.entries(packetStockByVariant).filter(([_, qty]) => qty > 0) : [];
                                  const hasStock = totalStock > 0;
                                  if (!hasStock) {
                                    return <div style={{ color: '#ff4444', backgroundColor: '#ffebee', padding: '6px 8px', borderRadius: '4px', border: '1px solid #ff4444', fontSize: '11px' }}><strong>⚠️ Out of Stock</strong></div>;
                                  }
                                  return (
                                    <div style={{ color: '#0a8938', backgroundColor: '#f8f9fa', padding: '6px 8px', borderRadius: '4px', border: '1px solid #0a8938', fontSize: '11px' }}>
                                      <div><strong>📦 Total Stock: {totalStock}</strong></div>
                                      {variantEntries.length > 0 && (
                                        <>
                                          <div style={{ fontSize: '10px', marginTop: '4px', color: '#555' }}>Breakdown by variant:</div>
                                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '2px' }}>
                                            {variantEntries.map(([variant, qty]) => (
                                              <span key={variant} style={{ backgroundColor: '#e8f5e9', padding: '2px 6px', borderRadius: '12px', fontSize: '9px', fontWeight: 'bold' }}>
                                                {variant}: {qty}
                                              </span>
                                            ))}
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  );
                                })()}

                                {isReelOrTota && stockByWeight && Object.keys(stockByWeight).length > 0 && !isUpdating && 
                                  <div style={{ fontSize: '10px', color: '#0a8938', marginTop: '2px', fontWeight: '500', backgroundColor: '#e8f5e9', padding: '2px 4px', borderRadius: '4px' }}>
                                    📊 {Object.entries(stockByWeight).map(([w, q]) => `${w}: ${q}`).join(', ')}
                                  </div>
                                }

                                {!isPacket && !isReelOrTota && stockQuantity !== undefined && p.productName && !isUpdating && 
                                  <div style={{ fontSize: '11px', color: parseFloat(stockQuantity) < 0 ? '#ff4444' : '#0a8938', marginTop: '2px', fontWeight: '500' }}>
                                    📦 Current Stock: {stockQuantity}
                                  </div>
                                }

                                {cat === 'roll' && !p.size && p.productName && !isUpdating && 
                                  <div style={{ fontSize: '11px', color: '#ff4444', marginTop: '2px', fontWeight: '500' }}>⚠️ Size is required for roll products</div>
                                }
                                {isReelOrTota && (!p.quantity || parseFloat(p.quantity) <= 0) && p.productName && !isUpdating && 
                                  <div style={{ fontSize: '11px', color: '#ff9800', marginTop: '2px', fontWeight: '500' }}>⚡ Enter weight in kg in the Quantity column</div>
                                }
                                {isReelOrTota && (!p.length || parseFloat(p.length) <= 0) && p.productName && !isUpdating && 
                                  <div style={{ fontSize: '11px', color: '#ff9800', marginTop: '2px', fontWeight: '500' }}>⚡ Length is required (e.g., meters)</div>
                                }
                              </td>

                              <td>
                                <input
                                  list={`categories-${idx}`}
                                  value={p.category || ""}
                                  onChange={(e) => handleUpdateProductChange(idx, "category", e.target.value)}
                                  className="read-only-input"
                                  disabled={isUpdating}
                                />
                                <datalist id={`categories-${idx}`}>
                                  {Array.isArray(categoryList) && categoryList.map((c, i) => <option key={i} value={c.categoryName} />)}
                                </datalist>
                              </td>

                              <td>
                                <input
                                  type="number"
                                  value={p.quantity ?? ""}
                                  onChange={(e) => handleUpdateProductChange(idx, "quantity", e.target.value)}
                                  className="read-only-input"
                                  disabled={isUpdating}
                                  placeholder={(cat === 'reel' || cat === 'tota') ? "Weight in kg" : "Return Qty"}
                                  step={(cat === 'reel' || cat === 'tota') ? "1" : "0.01"}
                                    onWheel={(e) => e.preventDefault()}   // ✅ add this

                                />
                              </td>

                              <td>
                                <input
                                  type="number"
                                  value={p.price ?? ""}
                                  onChange={(e) => handleUpdateProductChange(idx, "price", e.target.value)}
                                  className="read-only-input"
                                  disabled={isUpdating}
                                    onWheel={(e) => e.preventDefault()}   // ✅ add this

                                />
                              </td>

                              {/* SINGLE Size column in update form */}
                              {showSize && (
                                <td>
                                  {(isPacket || isRoll) ? (
                                    <input
                                      type="number"
                                      value={p.size ?? ""}
                                      onChange={(e) => handleUpdateProductChange(idx, "size", e.target.value)}
                                      className="read-only-input"
                                      placeholder={isRoll ? "Size (e.g., 18)" : "Size"}
                                      disabled={isUpdating}
                                      style={{ borderColor: (isRoll && !p.size && p.productName) ? '#ff4444' : '' }}
                                        onWheel={(e) => e.preventDefault()}   // ✅ add this

                                    />
                                  ) : isReelOrTota ? (
                                    <input
                                      type="number"
                                      step="any"
                                      value={p.length ?? ""}
                                      onChange={(e) => handleUpdateProductChange(idx, "length", e.target.value)}
                                      className="read-only-input"
                                      placeholder="Length (m)"
                                      disabled={isUpdating}
                                      style={{ borderColor: (!p.length && p.productName) ? '#ff4444' : '' }}
                                        onWheel={(e) => e.preventDefault()}   // ✅ add this

                                    />
                                  ) : (
                                    <input type="text" value="" className="read-only-input" disabled style={{ backgroundColor: '#f0f0f0' }} placeholder="N/A" />
                                  )}
                                </td>
                              )}

                              {showPacketColumns && (
                                <>
                                  <td>
                                    {isPacket ? (
                                      <input
                                        type="number"
                                        value={p.ktae ?? ""}
                                        onChange={(e) => handleUpdateProductChange(idx, "ktae", e.target.value)}
                                        className="read-only-input"
                                        placeholder="Ktae"
                                        disabled={isUpdating}
                                          onWheel={(e) => e.preventDefault()}   // ✅ add this

                                      />
                                    ) : (
                                      <input type="text" value="" className="read-only-input" disabled style={{ backgroundColor: '#f0f0f0' }} placeholder="N/A" />
                                    )}
                                  </td>
                                  <td>
                                    {isPacket ? (
                                      <input
                                        type="number"
                                        value={p.gram ?? ""}
                                        onChange={(e) => handleUpdateProductChange(idx, "gram", e.target.value)}
                                        className="read-only-input"
                                        placeholder="Gram"
                                        disabled={isUpdating}
                                          onWheel={(e) => e.preventDefault()}   // ✅ add this

                                      />
                                    ) : (
                                      <input type="text" value="" className="read-only-input" disabled style={{ backgroundColor: '#f0f0f0' }} placeholder="N/A" />
                                    )}
                                  </td>
                                </>
                              )}

                              {showBoxColumns && (
                                <>
                                  <td>
                                    {cat === 'box' ? (
                                      <input
                                        type="text"
                                        value={p.boxSize ?? ""}
                                        onChange={(e) => handleUpdateProductChange(idx, "boxSize", e.target.value)}
                                        className="read-only-input"
                                        placeholder="Box Size"
                                        disabled={isUpdating}
                                      />
                                    ) : (
                                      <input type="text" value="" className="read-only-input" disabled style={{ backgroundColor: '#f0f0f0' }} placeholder="N/A" />
                                    )}
                                  </td>
                                  <td>
                                    {cat === 'box' ? (
                                      <input
                                        type="text"
                                        value={p.particular ?? ""}
                                        onChange={(e) => handleUpdateProductChange(idx, "particular", e.target.value)}
                                        className="read-only-input"
                                        placeholder="Particular"
                                        disabled={isUpdating}
                                      />
                                    ) : (
                                      <input type="text" value="" className="read-only-input" disabled style={{ backgroundColor: '#f0f0f0' }} placeholder="N/A" />
                                    )}
                                  </td>
                                </>
                              )}

                              {showParticularColumn && (
                                <td>
                                  <input
                                    type="text"
                                    value={p.particular ?? ""}
                                    onChange={(e) => handleUpdateProductChange(idx, "particular", e.target.value)}
                                    className="read-only-input"
                                    placeholder="Particular"
                                    disabled={isUpdating}
                                  />
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="invoice-summary" style={{ color: "#000", marginTop: 10 }}>
                  <p><strong>Total Before GST:</strong> <input type="number" value={updateInvoice.totalBeforeGst ?? ""} onChange={(e) => handleUpdateInvoiceField("totalBeforeGst", e.target.value)} className="read-only-input" disabled={isUpdating} /></p>
                  <p>
                    <strong>GST Percentage:</strong>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <input type="number" value={updateInvoice.gstPercentage ?? ""} onChange={(e) => handleUpdateInvoiceField("gstPercentage", e.target.value)} className="read-only-input" disabled={isUpdating} />
                      <span>%</span>
                    </div>
                  </p>
                  <p><strong>GST Amount:</strong> <input type="number" value={updateInvoice.gstAmount ?? ""} onChange={(e) => handleUpdateInvoiceField("gstAmount", e.target.value)} className="read-only-input" disabled={isUpdating} /></p>

                  <p>
                    <strong>Vendor Name:</strong>
                    <input type="text" list="vendorNamesUpdate" value={updateInvoice.vendorName ?? ""} onChange={handleUpdateVendorChange} className="read-only-input" placeholder="Select Vendor" disabled={isUpdating} />
                    <datalist id="vendorNamesUpdate">
                      {vendorList.map(v => (
                        <option key={v.id} value={v.vendorName}>{v.vendorName} {v.currentBalance !== undefined ? `(Balance: ${v.currentBalance})` : ''}</option>
                      ))}
                    </datalist>
                    {selectedVendorBalance !== null && <span style={{ marginLeft: '10px', fontSize: '12px', color: '#0a8938' }}>Current Balance: Rs {selectedVendorBalance.toFixed(2)}</span>}
                  </p>

                  <p><strong>Discount:</strong> <input type="number" value={updateInvoice.invoiceDiscount ?? ""} onChange={(e) => handleUpdateInvoiceField("invoiceDiscount", e.target.value)} className="read-only-input" disabled={isUpdating} /></p>
                  <p><strong>Rent:</strong> <input type="number" value={updateInvoice.invoiceRent ?? ""} onChange={(e) => handleUpdateInvoiceField("invoiceRent", e.target.value)} className="read-only-input" disabled={isUpdating} /></p>
                  <p><strong>Amount Paid:</strong> <input type="number" value={updateInvoice.amountPaid ?? ""} onChange={(e) => handleUpdateInvoiceField("amountPaid", e.target.value)} className="read-only-input" disabled={isUpdating} /></p>
                  <p><strong>Description:</strong> <input type="text" value={updateInvoice.description ?? ""} onChange={(e) => handleUpdateInvoiceField("description", e.target.value)} className="read-only-input" style={{ width: "60%" }} disabled={isUpdating} /></p>
                  <p className="grand-total"><strong>Amount Payable:</strong> {updateInvoice.grandTotal ?? "0"}</p>
                </div>

                <div style={{ marginTop: 12, display: "flex", gap: 10 }}>
                  <button className="save-btn" onClick={saveUpdate} disabled={isUpdating} style={{ opacity: isUpdating ? 0.6 : 1, cursor: isUpdating ? 'not-allowed' : 'pointer' }}>{isUpdating ? 'Saving...' : 'Save Updated Invoice'}</button>
                  <button className="remove-btn" onClick={() => setUpdateInvoice(null)} disabled={isUpdating}>Cancel</button>
                </div>
              </div>
            )}

            <div className="invoice-summary" style={{ color: "#000", marginTop: 10 }}>
              <p><strong>Total Before GST:</strong> {invoice.totalBeforeGst ?? "0"}</p>
              <p><strong>GST Percentage:</strong> {invoice.gstPercentage ?? "0"}%</p>
              <p><strong>GST Amount:</strong> {invoice.gstAmount ?? "0"}</p>
              <p><strong>Discount:</strong> {invoice.invoiceDiscount ?? "0"}</p>
              <p><strong>Rent:</strong> {invoice.invoiceRent ?? "0"}</p>
              <p><strong>Amount Paid:</strong> {invoice.amountPaid ?? "0"}</p>
              <p><strong>Description:</strong> {invoice.description ?? "—"}</p>
              <p className="grand-total"><strong>Amount Payable:</strong> {invoice.grandTotal ?? "0"}</p>
            </div>
          </div>
        );
      })}

      <div style={{ position: "absolute", top: "-9999px" }}>
        <UniversalInvoicePrint ref={printRef} invoice={printInvoice} type="purchase" />
      </div>

      {fullScreenImage && (
        <div onClick={() => setFullScreenImage(null)} style={{
          position: "fixed", top: 0, left: 0, width: "100%", height: "100%",
          backgroundColor: "rgba(0,0,0,0.8)", display: "flex", justifyContent: "center",
          alignItems: "center", zIndex: 9999, cursor: "zoom-out"
        }}>
          <img src={fullScreenImage} alt="Full Screen Invoice" style={{ maxWidth: "90%", maxHeight: "90%", objectFit: "contain", boxShadow: "0 0 20px #fff" }} />
        </div>
      )}
    </div>
  );
}

export default ViewPurchaseReturn;